import { useEffect, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { Alert, Avatar, Box, Button, CircularProgress, Link, Paper, Tab, Tabs, Typography } from '@mui/material';
import { ArrowBack, Business, EditOutlined, EmailOutlined, History, Person, PhoneOutlined, Work } from '@mui/icons-material';
import BaseWrapper from '../../components/BaseWrapper/BaseWrapper';
import { API } from '../../lib/api';
import EditContactModal from './EditContactModal';
import ContactOpportunities from './ContactOpportunities';
import ContactHistory from './ContactHistory';
import CreateContactOpportunity from './CreateContactOpportunity';

const SingleContact = () => {
    const { id } = useParams();
    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [tab, setTab] = useState(0);
    const [editing, setEditing] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        setContact(null);
        setTab(0);
        setEditing(false);
        setSaved(false);
        API.Contacts.FetchSingle(id)
            .then((data) => { if (active) setContact(data); })
            .catch((err) => {
                if (active) setError(err.response?.data?.message || 'Unable to load contact. Please try again.');
            })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [id]);

    const fullName = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ') || contact?.name || 'Contact';
    const initials = fullName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase();
    const organization = contact?.customers?.map((customer) => customer.organizationName).filter(Boolean).join(', ');

    return <BaseWrapper content={
        <Box>
            <Button component={RouterLink} to="/contacts" variant="outlined" startIcon={<ArrowBack />}
                sx={{ borderRadius: 2, px: 2, py: 1, textTransform: 'none', fontWeight: 600 }}>
                Back to Contacts
            </Button>
            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress aria-label="Loading contact" /></Box>
                : error ? <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
                : contact && <>
                    <Typography variant="h4" sx={{ my: 3 }}>
                        {fullName}
                    </Typography>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="contact tabs"
                            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
                            sx={{ maxWidth: '100%', '& .MuiTabs-scroller': { overflowX: 'auto !important' }, '& .MuiTab-root': { flexShrink: 0, minWidth: { xs: 120, sm: 160 } } }}>
                            {[
                                ['Overview', <Person />], ['History', <History />], ['Opportunities', <Work />],
                            ].map(([label, icon], index) => <Tab key={label} label={label} icon={icon} id={`contact-tab-${index}`} aria-controls={`contact-tabpanel-${index}`} />)}
                        </Tabs>
                    </Box>
                    {[0, 1, 2].map((index) => <Box key={index} role="tabpanel" hidden={tab !== index} id={`contact-tabpanel-${index}`} aria-labelledby={`contact-tab-${index}`}>
                        {index === 1 && <ContactHistory key={id} contact={contact} active={tab === 1} />}
                        {tab === index && index === 2 && <ContactOpportunities contactId={id} />}
                        {tab === index && index === 0 && <Box sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ maxWidth: 760, display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                                <CreateContactOpportunity key={id} contact={contact} />
                                <Button variant="outlined" disabled={contact.canEdit === false} startIcon={<EditOutlined />} onClick={() => { setSaved(false); setEditing(true); }}
                                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}>Edit Contact</Button>
                            </Box>
                            {saved && <Alert severity="success" onClose={() => setSaved(false)} sx={{ maxWidth: 760, mb: 2 }}>Contact updated successfully.</Alert>}
                            <Paper component="article" aria-label={`${fullName} business card`} elevation={0}
                                sx={{ maxWidth: 760, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: '0 12px 36px rgba(0, 0, 0, 0.08)' }}>
                                <Box sx={{ height: 6, bgcolor: 'primary.main' }} />
                                <Box sx={{ p: { xs: 3, sm: 5 } }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                                        <Avatar sx={{ width: 80, height: 80, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 28, fontWeight: 600 }}>{initials}</Avatar>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="h4" component="h2" sx={{ fontWeight: 700, letterSpacing: '-0.5px', overflowWrap: 'anywhere' }}>{fullName}</Typography>
                                            <Typography sx={{ mt: 0.5, color: 'text.secondary', overflowWrap: 'anywhere' }}>{contact.jobTitle || contact.contactJobTitle || 'Job title not provided'}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: 'primary.main' }}>
                                                <Business fontSize="small" />
                                                <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{organization || 'Organization not provided'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
                                                {[
                                                    { action: 'Email', value: contact.primaryEmail, href: `mailto:${contact.primaryEmail}`, icon: <EmailOutlined /> },
                                                    { action: 'Call', value: contact.phoneNumber, href: `tel:${contact.phoneNumber}`, icon: <PhoneOutlined /> },
                                                ].filter(({ value }) => value).map(({ action, value, href, icon }) => (
                                                    <Button key={action} component="a" href={href} variant="outlined" size="small" startIcon={icon}
                                                        aria-label={`${action} ${fullName} at ${value}`}
                                                        sx={{ width: 104, flexShrink: 0, borderRadius: 2, minHeight: 40, textTransform: 'none', fontWeight: 600 }}>
                                                        {action}
                                                    </Button>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'grid', gap: 2.5 }}>
                                        {[
                                            { label: 'Primary email', action: 'Email', value: contact.primaryEmail, href: contact.primaryEmail ? `mailto:${contact.primaryEmail}` : null, icon: <EmailOutlined /> },
                                            { label: 'Phone', action: 'Call', value: contact.phoneNumber, href: contact.phoneNumber ? `tel:${contact.phoneNumber}` : null, icon: <PhoneOutlined /> },
                                        ].map(({ label, value, href, icon }) => <Box key={label} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                            <Box sx={{ display: 'flex', color: href ? 'primary.main' : 'text.secondary' }}>{icon}</Box>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                <Typography sx={{ overflowWrap: 'anywhere' }}>
                                                    {href ? <Link href={href} underline="none" color="inherit" sx={{ fontWeight: 400 }}>{value}</Link> : 'Not provided'}
                                                </Typography>
                                            </Box>
                                        </Box>)}
                                    </Box>
                                </Box>
                            </Paper>
                        </Box>}
                    </Box>)}
                    {editing && <EditContactModal key={contact._id} contact={contact} onClose={() => setEditing(false)}
                        onSaved={(updated) => {
                            setContact((previous) => ({ ...previous, ...updated }));
                            setEditing(false);
                            setSaved(true);
                        }} />}
                </>}
        </Box>
    } />;
};

export default SingleContact;
